import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../../components/Button/Button';
import { useCart } from '../../../contexts/CartContext';
import { resolveCjLink } from '../cjLinks';
import {
    getAvailableShopifyMethod,
    getOtherMethods,
} from '../purchaseMethods';
import type { ProductInfo, PurchaseMethod, SerializedVariant } from '../types';
import { BuyingOptionsModal } from './BuyingOptionsModal';
import './CTAButtons.css';

interface CTAButtonsProps {
    product: ProductInfo;
    variant: SerializedVariant | null;
    shopId: number;
}

function getShopLabel(pm: PurchaseMethod, brand: string | null): string {
    const retailer = pm.retailer_name
        ?? (pm.method_type === 'manual_affiliate' && brand ? brand : null);
    const base = retailer ? `shop on ${retailer}` : 'shop here';
    // The link stays clickable, but flag stock truthfully. Manual links have no
    // stock feed (availability sits at the DB default), so only surface this for
    // methods that actually report stock.
    const outOfStock = pm.method_type !== 'manual_affiliate' && pm.availability === 'out_of_stock';
    // The newline collapses to a space on desktop (white-space: normal) and is
    // preserved as a hard break on mobile (white-space: pre-line in the CSS).
    return outOfStock ? `${base}\n(unavailable)` : base;
}

export function CTAButtons({ product, variant, shopId }: CTAButtonsProps) {
    const [resolvedCjLinks, setResolvedCjLinks] = useState<Record<number, string>>({});
    const [attemptedVariants, setAttemptedVariants] = useState<Set<number>>(() => new Set());
    const inFlightRef = useRef<Set<number>>(new Set());
    const { addToCart, openCart } = useCart();
    const [addingToCart, setAddingToCart] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const shopifyMethod = variant ? getAvailableShopifyMethod(variant.purchase_methods) : null;
    const otherMethods = variant ? getOtherMethods(variant.purchase_methods) : [];
    const hasShopify = !!shopifyMethod;
    const hasOtherOptions = otherMethods.length > 0;

    const unresolvedCjMethod = otherMethods.find((m) => m.method_type === 'cj' && m.resolved === false);
    // The attribution endpoint needs the specific purchase method to attribute,
    // so skip resolving if the CJ method has no id.
    const cjPurchaseMethodId = unresolvedCjMethod?.id ?? null;
    const needsCjResolve = !!variant
        && cjPurchaseMethodId !== null
        && !(variant.id in resolvedCjLinks)
        && !attemptedVariants.has(variant.id);

    useEffect(() => {
        if (!variant || !needsCjResolve || cjPurchaseMethodId === null) { return; }
        if (inFlightRef.current.has(variant.id)) { return; }
        const variantId = variant.id;
        inFlightRef.current.add(variantId);
        let cancelled = false;
        (async () => {
            const url = await resolveCjLink(variantId, shopId, cjPurchaseMethodId);
            inFlightRef.current.delete(variantId);
            if (cancelled) { return; }
            if (url) {
                setResolvedCjLinks((prev) => ({ ...prev, [variantId]: url }));
            }
            setAttemptedVariants((prev) => {
                const next = new Set(prev);
                next.add(variantId);
                return next;
            });
        })();
        return () => { cancelled = true; };
    }, [variant, needsCjResolve, shopId, cjPurchaseMethodId]);

    if (!variant) {
        return null;
    }

    if (!hasShopify && !hasOtherOptions) {
        return (
            <div className="pdp-ctas">
                <button type="button" className="pdp-cta pdp-cta--unavailable" disabled>
                    unavailable
                </button>
            </div>
        );
    }

    // A single external option has nowhere to branch to, so skip the modal and send
    // the shopper straight to the retailer.
    if (!hasShopify && otherMethods.length === 1) {
        const pm = otherMethods[0];
        const isCj = pm.method_type === 'cj';
        const label = getShopLabel(pm, product.brand);
        const resolvedUrl = resolvedCjLinks[variant.id];
        const href = isCj && resolvedUrl ? resolvedUrl : (pm.link ?? '#');

        // CJ links are resolved per-variant on the fly, so while the URL is being
        // fetched (including right after a variant switch) disable the button and
        // shimmer until it lands.
        if (isCj && needsCjResolve) {
            return (
                <div className="pdp-ctas">
                    <Button
                        label={label}
                        variant="primary"
                        type="button"
                        disabled
                        className="pdp-cta pdp-cta--primary pdp-cta--shimmer"
                    />
                </div>
            );
        }

        return (
            <div className="pdp-ctas">
                <Button
                    label={label}
                    variant="primary"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="pdp-cta pdp-cta--primary"
                />
            </div>
        );
    }

    const buyingOptionsLabel = otherMethods.length > 0
        ? `see ${otherMethods.length}${hasShopify ? ' other' : ''} buying option${otherMethods.length === 1 ? '' : 's'}`
        : 'buying options';
    const primaryLabel = hasShopify ? 'add to cart' : buyingOptionsLabel;

    const cachedCjUrl = resolvedCjLinks[variant.id];
    const linkClasses = ['pdp-cta', 'pdp-cta--primary'];

    const onCjAttempted = () => {
        const variantId = variant.id;
        setAttemptedVariants((prev) => {
            if (prev.has(variantId)) { return prev; }
            const next = new Set(prev);
            next.add(variantId);
            return next;
        });
    };

    const showBuyingOptions = hasOtherOptions;

    const secondaryButton = hasShopify && showBuyingOptions ? (
        <button
            type="button"
            className="pdp-cta pdp-cta--secondary"
            onClick={() => setModalOpen(true)}
        >
            {buyingOptionsLabel}
        </button>
    ) : null;

    const modal = showBuyingOptions ? (
        <BuyingOptionsModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            product={product}
            variant={variant}
            shopId={shopId}
            resolvedCjUrl={cachedCjUrl}
            onCjAttempted={onCjAttempted}
        />
    ) : null;

    if (hasShopify) {
        const onShopifyClick = async () => {
            if (addingToCart || !variant) { return; }
            setAddingToCart(true);
            try {
                const ok = await addToCart(variant.id, shopId);
                if (ok) { openCart(); }
            } finally {
                setAddingToCart(false);
            }
        };
        return (
            <div className="pdp-ctas">
                <Button
                    label={addingToCart ? 'adding...' : primaryLabel}
                    variant="primary"
                    type="button"
                    onClick={onShopifyClick}
                    className={linkClasses.join(' ')}
                />
                {secondaryButton}
                {modal}
            </div>
        );
    }

    return (
        <div className="pdp-ctas">
            <Button
                label={primaryLabel}
                variant="primary"
                type="button"
                onClick={() => setModalOpen(true)}
                className={linkClasses.join(' ')}
            />
            {modal}
        </div>
    );
}
